/**
 * Patient account directory.
 *
 * Replaces `renderAccountsPage` (accounts.js) — a read-only roster, which is all
 * the backend supports: GET /api/sync/patients returns the list, and there is no
 * route to edit or delete a patient. The legacy page rendered edit buttons that
 * had no handler.
 */

import { useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Input,
  Modal,
  PageHeader,
  Panel,
  Spinner,
  StatCard,
} from '../../components/common';
import { useData } from '../../context/DataContext';
import { useAsync } from '../../hooks/useAsync';
import { useDebounced } from '../../hooks/useLocalStorage';
import { bookingApi, prescriptionApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { isoToDisplayDate, formatTime12 } from '../../domain/dates';
import { statusLabel, statusStyle, typeLabel } from '../../domain/bookings';
import { GoogleAccounts } from '../settings/GoogleAccounts';

/** An account that is not active, as the badge says it. */
const ACCOUNT_STATUS = { pending: 'بانتظار التفعيل', suspended: 'موقوف', inactive: 'غير نشط' };

export function AdminAccounts() {
  const { patients, patientsState, loadPatients } = useData();

  const [query, setQuery] = useState('');
  const [inspecting, setInspecting] = useState(null);
  const debouncedQuery = useDebounced(query, 250);

  const filtered = useMemo(() => {
    const needle = debouncedQuery.trim().toLowerCase();
    if (!needle) return patients;
    return patients.filter(
      (patient) =>
        String(patient.name).toLowerCase().includes(needle) ||
        String(patient.username).toLowerCase().includes(needle) ||
        String(patient.phone || '').includes(needle) ||
        String(patient.email || '').toLowerCase().includes(needle),
    );
  }, [patients, debouncedQuery]);

  const totals = useMemo(
    () => ({
      total: patients.length,
      active: patients.filter((p) => p.status === 'active').length,
      withPhone: patients.filter((p) => p.phone).length,
      withEmail: patients.filter((p) => p.email).length,
    }),
    [patients],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="الحسابات"
        subtitle={`${patients.length} مستخدم مسجل`}
        icon="user-cog"
        actions={
          <Button
            variant="ghost"
            icon="refresh-cw"
            onClick={loadPatients}
            loading={patientsState.loading}
          >
            تحديث
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon="users" label="إجمالي المستخدمين" value={totals.total} accent="cyan" />
        <StatCard icon="user-check" label="نشط" value={totals.active} accent="emerald" />
        <StatCard icon="phone" label="لديه رقم هاتف" value={totals.withPhone} accent="purple" />
        <StatCard icon="mail" label="لديه بريد" value={totals.withEmail} accent="amber" />
      </div>

      <Panel>
        <div className="relative">
          <Icon
            name="search"
            size={16}
            className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-slate-500"
          />
          <Input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="ابحث بالاسم أو الهاتف أو البريد..."
            className="ps-9"
          />
        </div>
      </Panel>

      {patientsState.error ? (
        <ErrorState error={patientsState.error} onRetry={loadPatients} />
      ) : patientsState.loading && patients.length === 0 ? (
        <Spinner label="جاري تحميل الحسابات..." />
      ) : filtered.length === 0 ? (
        <Panel>
          <EmptyState icon="user-x" title="لا توجد حسابات مطابقة" />
        </Panel>
      ) : (
        <Panel padded={false}>
          <ul className="divide-y divide-white/5">
            {filtered.map((patient) => (
              <li key={patient.id}>
                <button
                  type="button"
                  onClick={() => setInspecting(patient)}
                  className="flex w-full items-center gap-4 p-4 text-start transition-colors hover:bg-white/5"
                >
                  <Avatar name={patient.name} size={40} />

                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="truncate font-bold text-white">{patient.name}</span>
                      {patient.status && patient.status !== 'active' && (
                        <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300">
                          {ACCOUNT_STATUS[patient.status] || patient.status}
                        </Badge>
                      )}
                    </span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-x-3 text-xs text-slate-400">
                      <span dir="ltr">@{patient.username}</span>
                      {patient.phone && <span dir="ltr">{patient.phone}</span>}
                      {patient.email && <span dir="ltr">{patient.email}</span>}
                    </span>
                  </span>

                  <Icon name="chevron-left" size={18} className="shrink-0 text-slate-500" />
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <PatientDetailsModal patient={inspecting} onClose={() => setInspecting(null)} />
    </div>
  );
}

/* ========================================================================== */

/** A patient's booking and prescription history — the legacy page had no equivalent. */
function PatientDetailsModal({ patient, onClose }) {
  const bookingsState = useAsync(
    () => bookingApi.list({ patientId: patient.id }),
    [patient?.id],
    { initialData: [], enabled: Boolean(patient?.id) },
  );

  const prescriptionsState = useAsync(
    () => prescriptionApi.listForPatient(patient.id),
    [patient?.id],
    { initialData: [], enabled: Boolean(patient?.id) },
  );

  if (!patient) return null;

  const bookings = bookingsState.data || [];
  const prescriptions = prescriptionsState.data || [];
  const spend = bookings
    .filter((b) => b.status === 'completed')
    .reduce((sum, b) => sum + (Number(b.finalPrice) || 0), 0);

  return (
    <Modal open onClose={onClose} title="بيانات الحساب" icon="user" size="lg">
      <div className="space-y-5">
        <div className="flex items-center gap-4">
          <Avatar name={patient.name} size={56} />
          <div className="min-w-0">
            <h3 className="truncate text-lg font-bold text-white">{patient.name}</h3>
            <p className="text-sm text-slate-400" dir="ltr">
              @{patient.username}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-2 gap-3 rounded-xl border border-white/5 bg-white/5 p-3 text-sm">
          <Item label="الهاتف" value={patient.phone || '—'} ltr />
          <Item label="البريد" value={patient.email || '—'} ltr />
          <Item
            label="تاريخ التسجيل"
            value={patient.createdAt ? new Date(patient.createdAt).toLocaleDateString('ar-EG') : '—'}
          />
          <Item label="إجمالي الإنفاق" value={formatCurrency(spend)} />
        </dl>

        <section>
          <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200">
            <Icon name="calendar-check" size={15} className="text-cyan-400" />
            الحجوزات ({bookings.length})
          </h4>

          {bookingsState.loading ? (
            <Spinner variant="inline" label="جاري التحميل..." />
          ) : bookings.length === 0 ? (
            <p className="text-xs text-slate-500">لا توجد حجوزات.</p>
          ) : (
            <ul className="custom-scrollbar max-h-52 space-y-2 overflow-y-auto pe-1">
              {bookings.map((booking) => (
                <li
                  key={booking.id}
                  className="flex flex-wrap items-center gap-2 rounded-xl border border-white/5 bg-white/5 p-2.5"
                >
                  <span className="text-xs font-bold text-white">
                    {booking.doctorName || `طبيب #${booking.doctorId}`}
                  </span>
                  <Badge className={statusStyle(booking.status)}>{statusLabel(booking.status)}</Badge>
                  <span className="ms-auto text-[11px] text-slate-400">
                    {typeLabel(booking.type)} — {isoToDisplayDate(booking.date)} —{' '}
                    {formatTime12(booking.time)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200">
            <Icon name="file-text" size={15} className="text-purple-400" />
            الروشتات ({prescriptions.length})
          </h4>

          {prescriptionsState.loading ? (
            <Spinner variant="inline" label="جاري التحميل..." />
          ) : prescriptions.length === 0 ? (
            <p className="text-xs text-slate-500">لا توجد روشتات.</p>
          ) : (
            <ul className="custom-scrollbar max-h-52 space-y-2 overflow-y-auto pe-1">
              {prescriptions.map((prescription) => (
                <li
                  key={prescription.id}
                  className="rounded-xl border border-white/5 bg-white/5 p-2.5"
                >
                  <p className="text-xs font-bold text-white">
                    {prescription.PrescribingDoctor?.name || prescription.doctorName || 'طبيب'}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-400">
                    {prescription.diagnosis || 'بدون تشخيص'} —{' '}
                    {isoToDisplayDate(prescription.date) || prescription.date}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Attaching a Google address is the one thing an admin can actually
            change about a patient account — the rest of this modal is read-only
            because the backend offers no route to edit a patient. */}
        <section className="border-t border-white/10 pt-5">
          <h4 className="mb-3 flex items-center gap-2 text-sm font-bold text-slate-200">
            <Icon name="mail" size={15} className="text-red-400" />
            حسابات Google المرتبطة
          </h4>
          <GoogleAccounts userId={patient.id} ownerName={patient.name} embedded />
        </section>
      </div>
    </Modal>
  );
}

function Item({ label, value, ltr = false }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] text-slate-400">{label}</dt>
      <dd className="truncate text-xs font-bold text-slate-200" dir={ltr ? 'ltr' : undefined}>
        {value}
      </dd>
    </div>
  );
}

export default AdminAccounts;
